import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, Video, AlertCircle } from 'lucide-react';

interface PracticeVideoUploadProps {
  onUploadComplete: (videoId: string) => void;
}

export const PracticeVideoUpload: React.FC<PracticeVideoUploadProps> = ({
  onUploadComplete
}) => {
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    courseId: ''
  });
  const [courses, setCourses] = useState<any[]>([]);
  const [videoFile, setVideoFile] = useState<File | null>(null);

  React.useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    const { data } = await supabase
      .from('courses')
      .select('id, title')
      .eq('status', 'published')
      .order('title');
    
    if (data) setCourses(data);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('video/')) {
        toast({
          title: "Invalid file type",
          description: "Please select a video file",
          variant: "destructive"
        });
        return;
      }
      
      // Validate file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select a video file smaller than 100MB",
          variant: "destructive"
        });
        return;
      }
      
      setVideoFile(file);
    }
  };

  const uploadVideo = async () => {
    if (!videoFile || !formData.title) {
      toast({
        title: "Missing information",
        description: "Please provide a title and select a video file",
        variant: "destructive"
      });
      return;
    }

    setIsUploading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Upload video file to storage
      const fileExt = videoFile.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('practice-videos')
        .upload(fileName, videoFile);

      if (uploadError) throw uploadError;

      // Get video URL
      const { data: urlData } = supabase.storage
        .from('practice-videos')
        .getPublicUrl(fileName);

      // Create practice video record
      const { data: video, error: dbError } = await supabase
        .from('practice_videos')
        .insert({
          user_id: user.id,
          course_id: formData.courseId || null,
          title: formData.title,
          description: formData.description,
          video_url: urlData.publicUrl,
          video_file_name: fileName,
          status: 'processing'
        })
        .select()
        .single();

      if (dbError) throw dbError;

      toast({
        title: "Video uploaded successfully",
        description: "Your video will be analyzed shortly"
      });

      onUploadComplete(video.id);
      
      // Reset form
      setFormData({ title: '', description: '', courseId: '' });
      setVideoFile(null);

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Video className="h-5 w-5" />
          Upload Practice Video
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Title *
          </label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
            placeholder="Describe what you're practicing"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Related Course (Optional)
          </label>
          <Select
            value={formData.courseId}
            onValueChange={(value) => setFormData(prev => ({ ...prev, courseId: value }))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a course" />
            </SelectTrigger>
            <SelectContent>
              {courses.map(course => (
                <SelectItem key={course.id} value={course.id}>
                  {course.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Description
          </label>
          <Textarea
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            placeholder="What specific skills or techniques are you trying to improve?"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Video File *
          </label>
          <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
            {videoFile ? (
              <div className="space-y-2">
                <Video className="h-12 w-12 mx-auto text-primary" />
                <p className="font-medium">{videoFile.name}</p>
                <p className="text-sm text-muted-foreground">
                  {(videoFile.size / (1024 * 1024)).toFixed(1)} MB
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setVideoFile(null)}
                >
                  Remove
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                <p className="text-muted-foreground">
                  Click to upload or drag and drop
                </p>
                <p className="text-xs text-muted-foreground">
                  MP4, MOV, AVI up to 100MB
                </p>
              </div>
            )}
            <input
              type="file"
              accept="video/*"
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
        </div>

        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium">AI Coaching Analysis</p>
              <p className="text-muted-foreground">
                Your video will be analyzed using AI to provide personalized coaching feedback 
                based on educational research and best practices.
              </p>
            </div>
          </div>
        </div>

        <Button
          onClick={uploadVideo}
          disabled={isUploading || !videoFile || !formData.title}
          className="w-full"
        >
          {isUploading ? 'Uploading...' : 'Upload and Analyze Video'}
        </Button>
      </CardContent>
    </Card>
  );
};